(() => {
  const STORAGE_KEY = 'recently-viewed-products';
  const HARD_LIMIT = 50;

  function readHandles() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
    } catch (error) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      return;
    }
  }

  function trackCurrentProduct(handle) {
    if (!handle) return readHandles();
    const existing = readHandles().filter((h) => h !== handle);
    const updated = [handle, ...existing].slice(0, HARD_LIMIT);
    writeHandles(updated);
    return updated;
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.currentHandle = this.dataset.productHandle || '';
      this.maxProducts = parseInt(this.dataset.maxProducts, 10) || 8;
      this.minProducts = parseInt(this.dataset.minProducts, 10) || 1;
      this.slidesMobile = Number(this.dataset.slidesMobile) || 1;
      this.slidesTablet = Number(this.dataset.slidesTablet) || 2;
      this.slidesDesktop = Number(this.dataset.slidesDesktop) || 4;
      this.showSecondaryImage = this.dataset.showSecondaryImage === 'true';

      this.swiperEl = this.querySelector('[data-recently-viewed-swiper]');
      this.wrapper = this.querySelector('[data-recently-viewed-wrapper]');
      this.loadingEl = this.querySelector('[data-recently-viewed-loading]');
      this.progressEl = this.querySelector('[data-recently-viewed-progress]');

      this.init();
    }

    async init() {
      trackCurrentProduct(this.currentHandle);

      const handles = readHandles()
        .filter((handle) => handle !== this.currentHandle)
        .slice(0, this.maxProducts);

      if (handles.length < this.minProducts) {
        this.finish(false);
        return;
      }

      const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
      const validCards = cards.filter(Boolean);

      if (validCards.length < this.minProducts) {
        this.finish(false);
        return;
      }

      this.render(validCards);
      this.finish(true);
      this.initSlider();
    }

    async fetchCard(handle) {
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${rootUrl}products/${encodeURIComponent(handle)}?section_id=recently-viewed-card`;

      try {
        const response = await fetch(url);
        if (!response.ok) return null;

        const html = (await response.text()).trim();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const slide = parsed.querySelector('.swiper-slide');
        if (!slide) return null;

        if (!this.showSecondaryImage) {
          const hoverMedia = slide.querySelector('.media--hover-effect');
          const images = hoverMedia ? hoverMedia.querySelectorAll('img') : [];
          if (images.length > 1) images[images.length - 1].remove();
        }

        return slide.outerHTML;
      } catch (error) {
        return null;
      }
    }

    render(cardHtmlList) {
      const template = document.createElement('template');
      template.innerHTML = cardHtmlList.join('');
      this.wrapper.replaceChildren(template.content);
    }

    initSlider() {
      if (!this.swiperEl || typeof Swiper === 'undefined') {
        this.initCardMedia();
        return;
      }

      this.wrap = this.querySelector('.related-products__slider-wrap');

      const options = this.getSwiperOptions();
      const swiper = new Swiper(this.swiperEl, options);
      this.swiper = swiper;

      this.swiperEl.classList.add('related-products__swiper--initialized');
      this.bindProgress(swiper);

      swiper.on('resize', () => this.syncBleedParams(swiper));

      this.initCardMedia();
    }

    getBleedMetrics(wrap) {
      if (!wrap) return { bleedLeft: 0, bleedRight: 0, contentWidth: 0 };

      const rect = this.getBoundingClientRect();
      const styles = window.getComputedStyle(this);
      const padLeft = parseFloat(styles.paddingLeft) || 0;
      const padRight = parseFloat(styles.paddingRight) || 0;
      const contentWidth = Math.max(0, this.clientWidth - padLeft - padRight);
      const contentLeft = rect.left + padLeft;
      const contentRight = contentLeft + contentWidth;
      const viewportWidth = document.documentElement.clientWidth;
      const bleedLeft = Math.max(0, contentLeft);
      const bleedRight = Math.max(0, viewportWidth - contentRight);

      wrap.style.setProperty('--rv-bleed-left', `${bleedLeft}px`);
      wrap.style.setProperty('--rv-bleed-right', `${bleedRight}px`);
      wrap.style.setProperty('--rv-content-width', `${contentWidth}px`);

      return { bleedLeft, bleedRight, contentWidth };
    }

    getSpaceBetween() {
      const width = window.innerWidth;
      if (width >= 990) return 19;
      if (width >= 750) return 20;
      return 16;
    }

    getSlidesPerViewSetting() {
      const width = window.innerWidth;
      if (width >= 990) return this.slidesDesktop;
      if (width >= 750) return this.slidesTablet;
      return this.slidesMobile;
    }

    getTrackSlidesPerView(contentWidth, bleedLeft, bleedRight, perView, spaceBetween) {
      if (!contentWidth || !perView) return perView;
      const slideWidth = (contentWidth - (perView - 1) * spaceBetween) / perView;
      if (slideWidth <= 0) return perView;
      const trackWidth = contentWidth + bleedLeft + bleedRight;
      return (trackWidth + spaceBetween) / (slideWidth + spaceBetween);
    }

    applyBleedLayout() {
      const { bleedLeft, bleedRight, contentWidth } = this.getBleedMetrics(this.wrap);
      const perView = this.getSlidesPerViewSetting();
      const spaceBetween = this.getSpaceBetween();
      const slidesPerView = this.getTrackSlidesPerView(
        contentWidth,
        bleedLeft,
        bleedRight,
        perView,
        spaceBetween
      );

      return {
        bleedLeft,
        bleedRight,
        contentWidth,
        spaceBetween,
        slidesPerView,
        slidesOffsetBefore: bleedLeft,
        slidesOffsetAfter: bleedRight,
      };
    }

    getSwiperOptions() {
      const { spaceBetween, slidesPerView, slidesOffsetBefore, slidesOffsetAfter } =
        this.applyBleedLayout();

      return {
        speed: 500,
        loop: false,
        slidesPerView,
        spaceBetween,
        slidesOffsetBefore,
        slidesOffsetAfter,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
      };
    }

    syncBleedParams(swiper) {
      const layout = this.applyBleedLayout();
      swiper.params.slidesPerView = layout.slidesPerView;
      swiper.params.spaceBetween = layout.spaceBetween;
      swiper.params.slidesOffsetBefore = layout.slidesOffsetBefore;
      swiper.params.slidesOffsetAfter = layout.slidesOffsetAfter;
      swiper.update();
    }

    bindProgress(swiper) {
      const fill = this.progressEl?.querySelector('.related-products__progress-fill');
      if (!this.progressEl || !fill) return;

      const update = () => {
        if (!swiper.slides.length) return;

        this.progressEl.hidden = Boolean(swiper.isLocked);

        const total = swiper.slides.length;
        const ratio = typeof swiper.progress === 'number' ? swiper.progress : 0;
        const minPct = Math.min((3 / Math.max(total, 3)) * 100, 40);

        if (ratio <= 0.001) {
          fill.style.width = `${Math.max(minPct, 8)}%`;
        } else if (swiper.isEnd || ratio >= 0.999) {
          fill.style.width = '100%';
        } else {
          fill.style.width = `${Math.min(Math.max(ratio * 100, minPct), 100)}%`;
        }
      };

      swiper.on('slideChange', update);
      swiper.on('progress', update);
      swiper.on('resize', update);
      update();
    }

    initCardMedia() {
      if (typeof window.initCardMediaSliders === 'function') {
        window.initCardMediaSliders(this);
      }
    }

    finish(hasProducts) {
      if (this.loadingEl) this.loadingEl.remove();

      if (hasProducts) {
        this.hidden = false;
      } else {
        this.remove();
      }
    }
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();
